/* eslint-disable no-unused-expressions */
import * as React from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import { MenubarContext } from "../../contexts/Menubar";
import { usePrevious } from "../../hooks/usePrevious";
import MenuItem from "./MenuItem";
import Submenu from "./Submenu";

/*type MenuItem = {
  id: number;
  name?: string;
  expand: () => void;
  collapse: () => void;
  children?: Menuitem[];
  parentId: number | null;
};*/

const Menubar = ({ children, direction, onKeyDown, ...props }) => {
  const id = React.useRef(_.uniqueId("menubar--")).current;
  const menuItems = React.useRef([]).current;
  const menuRef = React.useRef(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const previousIndex = usePrevious(currentIndex) ?? 0;
  const [activeMenuItemIds, setActiveMenuItemIds] = React.useState(new Set());
  const addMenuItem = React.useCallback((id, parentId, name = '') => {
    const newMenuItem = {
        id,
        name,
        children: [],
        isTrigger: false,
        parentId
    };

    if (parentId === null) {
      menuItems.push(newMenuItem);
      return;
    }

    function findAndAdd(items) {
      for (const item of items) {
        if (item.id === parentId) {
          item.children.push(newMenuItem);
          return true;
        }

        if (findAndAdd(item.children)) {
          return true;
        }
      }
      return false;
    }

    findAndAdd(menuItems);  
  }, [menuItems]);

  const removeMenuItem = React.useCallback((id) => {
    const removeItem = (_id, items) => {
      for (const item of items) {
        if (item.id === _id) {
          items.splice(items.indexOf(item), 1);
          return true;
        }

        if (removeItem(id, item.children)) {
          return true;
        }
      }
    };

    removeItem(id, menuItems);
  }, [menuItems]);

  const addExpandAndCollapseFunctionByTrigger = React.useCallback((id, expand, collapse) => {
    function findAndAdd(items) {
      for (const item of items) {
        if (item.id === id) {
          if (expand) item.expand = expand;
          if (collapse) item.collapse = collapse;
          item.isTrigger = true;

          return true;
        }

        if (findAndAdd(item.children)) {
          return true;
        }
      }
      return false;
    }

    findAndAdd(menuItems); 
  }, [menuItems]);

  const addActiveMenuItemId = React.useCallback((id) => {
    setActiveMenuItemIds(new Set([...activeMenuItemIds, id]));
  }, [activeMenuItemIds]);

  const removeActiveMenuItemId = React.useCallback((id) => {
    setActiveMenuItemIds(new Set([...activeMenuItemIds].filter((activeId) => activeId !== id)));
  }, [activeMenuItemIds]);

  const isTrigger = React.useCallback((id) => {
    const getItem = (id, items) => {
      for (const item of items) {
        if (item.id === id) return item;

        const foundedItem = getItem(id, item.children);
        if (foundedItem) return foundedItem;
      }
    };

    const menuItem = getItem(id, menuItems);

    return menuItem ? menuItem.isTrigger : false;
  }, [menuItems]);

  const closeSubtree = React.useCallback((parentId) => {
    const getItem = (id, items) => {
      for (const item of items) {
        if (item.id === id) {
          return item;
        }

        const foundedItem = getItem(id, item.children);
        if (foundedItem) return foundedItem
      }
    }

    const parent = getItem(parentId, menuItems);

    if (!parent || !parent?.children) return;

    const _closeSubtree = (item) => {
      for (const _item of item.children) {
        if (_item.isTrigger) {
          _item?.collapse();
        }

        _closeSubtree(_item);
      }
    };

    _closeSubtree(parent);
  }, [menuItems]);

  /**
   * Minden gyölér menüelem bezár, kivétel a paraméterben megadott azonosítójú menüelemhez tartozó gyökeret.
   * @param {*} exceptionId 
   */
  const closeRootMenuItems = React.useCallback((exceptionId) => {
    function findRootFromTree(roots, targetId) {

      for (const root of roots) {
        if (contains(root, targetId)) {
          return root;
        }
      }

      return null;
    }

    function contains(node, targetId) {
      if (node.id === targetId) return true;

      for (const child of node.children) {
        if (contains(child, targetId)) return true;
      }

      return false;
    }

    const root = findRootFromTree(menuItems, exceptionId);

    if (!root) return;

    for (const item of menuItems) {
      if (item.id !== root.id) {
        closeSubtree(item.id);
        item.collapse?.();
      }
    }
  }, [menuItems, closeSubtree]);

  const first = () => setCurrentIndex(0);

  const last = () => setCurrentIndex(menuItems.size - 1);

  const next = () => {
    const index = currentIndex === menuItems.size - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(index);
  };

  const previous = () => {
    const index = currentIndex === 0 ? menuItems.size - 1 : currentIndex - 1;
    setCurrentIndex(index);
  };

  const match = (e) => {
    const items = Array.from(menuItems);

    // Reorder the array, starting with the currentNode
    const reorderedItems = [
      ...items.slice(currentIndex),
      ...items.slice(0, currentIndex)
    ];

    // Find all nodes that begin with the pressed letter
    const matches = reorderedItems.filter((menuItem) => {
      const { textContent } = menuItem.firstChild;
      const firstLetter = textContent.toLowerCase().charAt(0);
      return e.key === firstLetter;
    });

    // Exit early if there are no matches
    if (!matches.length) {
      return;
    }

    // If the focused item is a match, focus the next match.
    // Otherwise, focus the first match
    const currentNode = items[currentIndex];
    const nextMatch = matches.includes(currentNode) ? matches[1] : matches[0];
    const index = items.findIndex((item) => {
      return item === nextMatch;
    });

    setCurrentIndex(index);
  };

  const keyDown = (e) => {
    e.stopPropagation();

    if (direction === "vertical") {
      switch (e.code) {
        case "ArrowUp":
          e.preventDefault();
          previous();
          break;
        case "ArrowDown":
          e.preventDefault();
          next();
          break;
        case "ArrowRight":
          e.preventDefault();
          const isFromSubmenu =
            e.target?.parentNode?.getAttribute(
              "data-menubar-submenu-listitem"
            ) === "";

          if (isFromSubmenu) {
            next();
          }
          break;
        default:
          break;
      }
    }

    if (direction === "horizontal") {
      switch (e.code) {
        case "ArrowLeft":
          e.preventDefault();
          previous(e);
          break;
        case "ArrowRight":
          e.preventDefault();
          next(e);
          break;
        case "ArrowDown":
          e.stopPropagation();
          //e.preventDefault();
          break;
        default:
          break;
      }
    }

    switch (e.code) {
      case "End":
        e.preventDefault();
        last();
        break;
      case "Home":
        e.preventDefault();
        first();
        break;
      default:
        match(e);
        break;
    }
  };

  const listProps = {
    ...props,
    id,
    role: "menubar",
    "aria-orientation": direction,
    "data-menubar-list": "",
    onKeyDown: (e) => {
      onKeyDown?.(e);
      //keyDown(e);
    }
  };

  /*React.useEffect(() => {
    const targetSet = new Set(activeMenuItemIds);

    function walk(items) {
      let hasMatch = false;

      for (const item of items) {
        const childMatch = walk(item.children);

        if (targetSet.has(item.id) || childMatch) {
          if (item.expand) item.expand();
            hasMatch = true;
        } else {
          if (item.collapse) item.collapse();
        }
      }

      return hasMatch;
    }

    walk(menuItems);
  }, [menuItems, activeMenuItemIds]);*/

  /*React.useEffect(() => {
    if (currentIndex !== previousIndex) {
      const items = Array.from(menuItems);
      const currentNode = items[currentIndex]?.firstChild;
      const previousNode = items[previousIndex]?.firstChild;

      // https://www.w3.org/TR/wai-aria-practices/#kbd_roving_tabindex
      previousNode?.setAttribute("tabindex", "-1");
      currentNode?.setAttribute("tabindex", "0");
      currentNode?.focus();
    }
  }, [currentIndex, previousIndex, menuItems]);*/

  const value = React.useMemo(() => ({
    menuItems,
    addMenuItem,
    removeMenuItem,
    addExpandAndCollapseFunctionByTrigger,
    direction,
    menuRef,
    addActiveMenuItemId,
    removeActiveMenuItemId,
    isTrigger,
    closeSubtree,
    closeRootMenuItems
  }), [
    menuItems,
    direction,
    menuRef,
    addMenuItem,
    removeMenuItem,
    addExpandAndCollapseFunctionByTrigger,
    addActiveMenuItemId,
    removeActiveMenuItemId,
    isTrigger,
    closeSubtree,
    closeRootMenuItems
  ]);

  return (
    <MenubarContext.Provider value={value}>
      <ul ref={menuRef} className={`menubar ${direction}`} {...listProps}>
        {children}
      </ul>
    </MenubarContext.Provider>
  );
};

Menubar.propTypes = {
  children: PropTypes.node.isRequired,
  direction: PropTypes.string,
  onKeyDown: PropTypes.func
};

Menubar.defaultProps = {
  direction: "horizontal"
};

Menubar.MenuItem = MenuItem;
Menubar.Submenu = Submenu;
Menubar.Submenu.Trigger = Submenu.Trigger;
Menubar.Submenu.List = Submenu.List;

export default Menubar;
